/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
'use client';

/**
 * MapAddressSearch — ô tìm kiếm autocomplete NỔI trên map. Tái dùng
 * `useAutocomplete` sẵn có. Chọn 1 gợi ý → resolve toạ độ (từ gợi ý hoặc
 * `getBatchGeocodeFn`) → gọi `onPick({longitude, latitude})` để cha flyTo tới
 * điểm đó; hook reverse-geocode-on-idle sẽ tự cập nhật lại địa chỉ ở tâm.
 */
import { Input } from '@ahafood/ui/components/input';
import { useDebouncedValue } from '@ahafood/ui/hooks/use-debounced-value';
import type { Address } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { Loader2, MapPin, Search, X } from 'lucide-react';
import { useState } from 'react';
import { getBatchGeocodeFn } from '@/api/ahafood/get-batch-geocode-fn';
import { useAutocomplete } from '@/api/use-autocomplete';

export type MapAddressSearchProps = {
  /** Toạ độ tâm map hiện tại để bias kết quả gần vị trí đang xem. */
  biasCenter?: { lat: number; lng: number };
  /** Được gọi khi user chọn 1 gợi ý (đã resolve toạ độ). */
  onPick: (coords: { longitude: number; latitude: number }) => void;
  className?: string;
};

export default function MapAddressSearch({
  biasCenter,
  onPick,
  className,
}: MapAddressSearchProps) {
  const [keyword, setKeyword] = useState('');
  const [open, setOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [debounced] = useDebouncedValue(keyword, 400);

  const enabled = debounced.trim().length >= 2;
  const { data: suggestions = [], isFetching } = useAutocomplete(
    { input: debounced.trim(), lat: biasCenter?.lat, lng: biasCenter?.lng },
    { enabled }
  );

  const handleSelect = async (item: Address) => {
    setKeyword(item.address ?? '');
    setOpen(false);

    // Đã có toạ độ → dùng luôn.
    if (item.lat && item.lng) {
      onPick({ longitude: item.lng, latitude: item.lat });
      return;
    }

    // Chưa có toạ độ → forward-geocode text sang lat/lng.
    setResolving(true);
    try {
      const resolved = await getBatchGeocodeFn(item.address);
      if (resolved?.lat && resolved?.lng) {
        onPick({ longitude: resolved.lng, latitude: resolved.lat });
      }
    } catch (error) {
      console.error('❌ MapAddressSearch: resolve coords failed:', error);
    } finally {
      setResolving(false);
    }
  };

  const clear = () => {
    setKeyword('');
    setOpen(false);
  };

  const showDropdown = open && enabled && suggestions.length > 0;

  return (
    <div className={cn('relative w-full', className)}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={keyword}
          onChange={(e) => {
            setKeyword(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 150)}
          placeholder="Tìm địa chỉ, toà nhà, quán ăn..."
          className="h-11 rounded-xl border-none bg-white px-9 shadow-lg shadow-black/5 focus-visible:ring-2 focus-visible:ring-primary/40"
          aria-label="Tìm địa chỉ trên bản đồ"
        />
        {(isFetching || resolving) && (
          // Bọc span định vị riêng: animate-spin ghi đè transform nên nếu đặt
          // -translate-y-1/2 cùng element, icon sẽ nhảy vị trí thay vì xoay.
          <span className="absolute right-3 top-1/2 -translate-y-1/2">
            <Loader2 className="block size-4 animate-spin text-muted-foreground" />
          </span>
        )}
        {!isFetching && !resolving && keyword.length > 0 && (
          <button
            type="button"
            onClick={clear}
            className="absolute right-2 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
            aria-label="Xoá tìm kiếm"
          >
            <X className="size-4" />
          </button>
        )}
      </div>

      {showDropdown && (
        <ul className="absolute inset-x-0 top-[calc(100%+8px)] z-20 max-h-64 overflow-y-auto rounded-xl bg-white py-1 shadow-xl shadow-black/10">
          {suggestions.map((item, index) => (
            <li key={item.place_id ?? `${item.address}-${index}`}>
              <button
                type="button"
                // onMouseDown để chạy trước onBlur của input (tránh mất chọn).
                onMouseDown={(e) => {
                  e.preventDefault();
                  void handleSelect(item);
                }}
                className="flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/60"
              >
                <MapPin className="mt-0.5 size-4 shrink-0 text-primary" />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-foreground">
                    {item.short_address || item.address}
                  </span>
                  {(item.second_label || item.description) && (
                    <span className="block truncate text-xs text-muted-foreground">
                      {item.second_label || item.description}
                    </span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
